import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { API_BASE } from './config';

interface AuthContextValue {
  user: any | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // 防止短时间内多次调用 /me：合并刷新请求 + 最小间隔节流
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const refreshQueuedRef = useRef<boolean>(false);
  const lastRefreshAtRef = useRef<number>(0);

  const refreshUser = useCallback(async () => {
    // 若正在刷新，则合并为一次尾随刷新
    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      return refreshInFlightRef.current;
    }

    const REFRESH_MIN_INTERVAL_MS = 800;
    const runOnce = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Unauthorized');
        const data = await res.json();
        console.log('[AuthContext] 用户数据已更新:', data);
        setUser(data);
      } catch (err) {
        console.error('[AuthContext] 获取用户信息失败:', err);
        localStorage.removeItem('access_token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    const start = async () => {
      const now = Date.now();
      const sinceLast = now - lastRefreshAtRef.current;
      const waitMs = Math.max(0, REFRESH_MIN_INTERVAL_MS - sinceLast);
      if (waitMs > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, waitMs));
      }
      await runOnce();
      lastRefreshAtRef.current = Date.now();
    };

    const p = start().finally(() => {
      refreshInFlightRef.current = null;
      // 若刷新期间又触发了刷新请求，做一次尾随刷新（合并后只会再发起一次）
      if (refreshQueuedRef.current) {
        refreshQueuedRef.current = false;
        void refreshUser();
      }
    });

    refreshInFlightRef.current = p;
    return p;
  }, []);

  useEffect(() => {
    // 初次挂载时拉取一次
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
  };

  const value: AuthContextValue = { user, loading, refreshUser, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
